import { useState, useEffect } from "react";
import { Divider, Select } from "antd";
import { Text, Button } from "../../general";
import Chevron from "../../systemIcons/Chevron/Chevron";
import styled from "styled-components";
import { PlusOutlined } from "@ant-design/icons";
import Input from "../Input";

export type OptionType<T> = {
  value: T;
  label: string;
};

export type FieldConfig = {
  key: string;
  placeholder: string;
  type?: "text" | "number";
  size?: number; // percentage of the container width (0-100)
};

export type SelectAddOptionProps<T> = {
  placeholder?: string;
  options: OptionType<T>[];
  inputPlaceholder?: string;
  selectedValue: T | undefined;
  setSelectedValue: (value: T) => void;
  valueKey?: keyof T;
  fields?: FieldConfig[];
  'data-testid'?: string;
};

const SelectAddOption = <T,>({
  placeholder,
  options,
  inputPlaceholder,
  selectedValue,
  setSelectedValue,
  valueKey,
  fields,
  'data-testid': dataTestId,
}: SelectAddOptionProps<T>) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState(options);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setDropdownOptions((prevOptions) => {
      const existingLabels = new Set(prevOptions.map((opt) => opt.label));
      const newOptions = options.filter(
        (opt) => !existingLabels.has(opt.label),
      );
      return [...prevOptions, ...newOptions];
    });
  }, [options]);

  const getOptionValue = (option: OptionType<T>) => {
    if (valueKey && typeof option.value === "object" && option.value !== null) {
      return option.value[valueKey];
    }
    return option.value;
  };

  const handleChange = (value: any) => {
    const selectedOption = dropdownOptions.find(
      (opt) => getOptionValue(opt) === value,
    );
    if (selectedOption) {
      setSelectedValue(selectedOption.value);
    }
    setDropdownOpen(false);
  };

  const handleInputChange = (key: string, value: string) => {
    setInputValues((prev) => ({ ...prev, [key]: value }));
    // Clear error when input changes
    setErrorMessage(null);
  };

  const handleAddItemClick = () => {
    if (!fields) {
      const inputValue = inputValues.input?.trim();
      if (!inputValue) return;

      const isDuplicate = dropdownOptions.some(
        (option) => option.label.toLowerCase() === inputValue.toLowerCase(),
      );

      if (isDuplicate) {
        setErrorMessage("This name already exists");
        return;
      }

      const newValue = valueKey
        ? ({ [valueKey]: inputValue } as T)
        : (inputValue as T);

      setDropdownOptions((prevOptions) => [
        ...prevOptions,
        { label: inputValue, value: newValue },
      ]);
      setSelectedValue(newValue);
      setInputValues({});
      setDropdownOpen(false);
      return;
    }

    // Handle object case with multiple fields
    const hasEmptyFields = fields.some(
      (field) => !inputValues[field.key]?.trim(),
    );
    if (hasEmptyFields) {
      setErrorMessage("This field cannot be empty");
      return;
    }

    const newValue = {
      ...Object.fromEntries(
        fields.map((field) => [field.key, inputValues[field.key]]),
      ),
      ...(valueKey ? { [valueKey]: inputValues[valueKey as string] } : {}),
    } as T;

    const nameField = fields.find((field) => field.key === "name");
    const label = nameField ? inputValues.name : inputValues[fields[0].key];

    const isDuplicate = dropdownOptions.some(
      (option) => option.label.toLowerCase() === label.toLowerCase(),
    );

    if (isDuplicate) {
      setErrorMessage("This name already exists");
      return;
    }

    const newOption = { label, value: newValue };
    setDropdownOptions((prevOptions) => [...prevOptions, newOption]);
    setSelectedValue(newValue);
    setInputValues({});
    setDropdownOpen(false);
  };

  const getDisplayValue = (value: T | undefined) => {
    if (!value) return undefined;
    if (typeof value === "object" && value !== null) {
      const nameField = fields?.find((field) => field.key === "name");
      return nameField
        ? value[nameField.key as keyof T]
        : value[fields?.[0].key as keyof T];
    }
    return value;
  };

  const renderAddForm = () => {
    if (!fields) {
      return (
        <Input
          placeholder={inputPlaceholder}
          value={inputValues.input || ""}
          onChange={(e) => handleInputChange("input", e.target.value)}
        />
      );
    }

    return (
      <>
        {fields.map((field) => (
          <StyledInput
            key={field.key}
            type={field.type}
            placeholder={field.placeholder}
            value={inputValues[field.key] || ""}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            $size={field.size}
          />
        ))}
      </>
    );
  };

  return (
    <Select
      data-testid={dataTestId}
      value={getDisplayValue(selectedValue)}
      placeholder={placeholder ? <Text>{placeholder}</Text> : undefined}
      suffixIcon={<Chevron direction="down" />}
      open={dropdownOpen}
      onDropdownVisibleChange={(open) => setDropdownOpen(open)}
      onChange={handleChange}
      dropdownRender={(menuOptions) => (
        <>
          {menuOptions}
          <Footer>
            <StyledDivider />
            <FooterContent>
              <div style={{ width: "100%" }}>
                <InputsContainer>{renderAddForm()}</InputsContainer>
                {errorMessage && (
                  <div
                    style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
                  >
                    {errorMessage}
                  </div>
                )}
              </div>
              <Button variant="text" onClick={handleAddItemClick}>
                <ButtonContent>
                  <PlusOutlined />
                  <Text>{fields ? "Add" : "Add item"}</Text>
                </ButtonContent>
              </Button>
            </FooterContent>
          </Footer>
        </>
      )}
    >
      {dropdownOptions.map((option) => (
        <Select.Option key={option.label} value={getOptionValue(option)}>
          <Text>{option.label}</Text>
        </Select.Option>
      ))}
    </Select>
  );
};

const Footer = styled.div`
  padding: 0 12px;
`;
const StyledDivider = styled(Divider)`
  margin: 0;
`;
const FooterContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin: 8px 0;
`;

const ButtonContent = styled.div`
  width: fit-content;
  white-space: nowrap;
  display: flex;
  gap: 4px;
`;

const InputsContainer = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;
const StyledInput = styled(Input)<{ $size?: number }>`
  width: ${({ $size }) => ($size ? `${$size}%` : "100%")};
`;

export default SelectAddOption;
